import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Field } from "./parts";
import { Leaf, Play, FileText, AlertTriangle, ClipboardList, X, Trash2 } from "lucide-react";

const TABS = [
  { key: "today", label: "Today's Scouting" },
  { key: "upcoming", label: "Upcoming" },
  { key: "drafts", label: "Drafts" },
  { key: "review", label: "Needs Review" },
  { key: "completed", label: "Completed" },
];

export default function ScoutingHome() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isManager = ["admin", "supervisor"].includes(user.role);
  const [dash, setDash] = useState(null);
  const [tab, setTab] = useState("today");
  const [starting, setStarting] = useState(false);
  const [clients, setClients] = useState([]);
  const [ranches, setRanches] = useState([]);
  const [form, setForm] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState([]);
  const [timelineFor, setTimelineFor] = useState(null);
  const [followups, setFollowups] = useState(null);
  const listRef = React.useRef(null);

  const load = () => api.get("/scouting/dashboard").then(({ data }) => setDash(data));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const openStart = async () => {
    const [c, r] = await Promise.all([api.get("/clients"), api.get("/ranches")]);
    setClients(c.data.filter((x) => x.active !== false));
    setRanches(r.data);
    setForm({ client_id: "", ranch_number: "", visit_date: new Date().toISOString().slice(0, 10), scope: "entire", block_ids: [] });
  };

  const vineyards = [...new Map(ranches.map((r) => [r.ranch_number, r])).values()];
  const blocksFor = (rn) => ranches.filter((r) => r.ranch_number === rn);

  const start = async () => {
    if (!form.ranch_number) return;
    setStarting(true);
    try {
      const client = clients.find((c) => c.id === form.client_id);
      const { data } = await api.post("/scouting/visits", {
        client_id: form.client_id || null, client_name: client?.name || null,
        ranch_number: form.ranch_number, visit_date: form.visit_date,
        block_ids: form.scope === "blocks" ? form.block_ids : [],
      });
      navigate(`/scouting/visit/${data.id}`);
    } finally { setStarting(false); }
  };

  const openVisit = (v) => {
    if (v.status === "scout_complete" && isManager) navigate(`/scouting/review/${v.id}`);
    else navigate(`/scouting/visit/${v.id}`);
  };

  const openHistory = async (vy) => {
    const { data } = await api.get(`/scouting/reports?ranch_number=${encodeURIComponent(vy.ranch_number)}`);
    setHistory(data);
    setHistoryFor(vy);
  };

  if (!dash) return <div className="p-8 font-bold uppercase text-sm">Loading…</div>;
  const list = dash.lists[tab] || [];

  return (
    <div className="space-y-6" data-testid="scouting-home">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>Field Scouting</h1>
          <p className="text-stone-500 text-sm mt-1">Walk it once. Document, task, schedule and report from the same visit.</p>
        </div>
        <button data-testid="start-scouting-btn" onClick={openStart}
          className="h-14 px-6 bg-[#1F3D34] text-white font-bold uppercase flex items-center gap-2"><Play size={18} /> Start Scouting</button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {[
          { label: "Scheduled Today", value: dash.today.scheduled, icon: Leaf, testId: "stat-scheduled-today",
            onClick: () => { setTab("today"); listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); } },
          { label: "Completed Today", value: dash.today.completed, icon: Leaf, testId: "stat-completed-today",
            onClick: () => { setTab("completed"); listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); } },
          { label: "Remaining Today", value: dash.today.remaining, icon: Leaf, testId: "stat-remaining-today",
            onClick: () => { setTab("today"); listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); } },
          { label: "Follow-Up Required", value: dash.followup_observations, icon: AlertTriangle, warn: dash.followup_observations > 0, testId: "stat-followup",
            onClick: async () => { const { data } = await api.get("/scouting/followups"); setFollowups(data); } },
          { label: "Open Tasks From Scouting", value: dash.open_scouting_tasks, icon: ClipboardList, testId: "stat-open-tasks",
            onClick: () => navigate("/worklist") },
        ].map((s) => (
          <button key={s.label} type="button" data-testid={s.testId} onClick={s.onClick}
            className={`bg-white border p-4 text-left transition-colors hover:border-[#1F3D34] ${s.warn ? "border-amber-400" : "border-stone-200"}`}>
            <div className="text-[10px] font-bold uppercase tracking-wide text-stone-500">{s.label}</div>
            <div className="text-3xl font-bold mt-1" style={{ fontFamily: "'Fraunces', serif" }}>{s.value}</div>
          </button>
        ))}
      </div>

      {/* Visit lists */}
      <div ref={listRef} className="bg-white border border-stone-200">
        <div className="flex overflow-x-auto border-b border-stone-200">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`px-4 py-3 text-xs font-bold uppercase whitespace-nowrap ${tab === t.key ? "bg-[#1F3D34] text-white" : "text-stone-600 hover:bg-stone-50"}`}>
              {t.label} ({(dash.lists[t.key] || []).length})
            </button>
          ))}
        </div>
        <div className="divide-y divide-stone-100">
          {list.length === 0 && <div className="p-6 text-center text-stone-400 text-sm font-bold uppercase">Nothing here</div>}
          {list.map((v) => (
            <div key={v.id} className="flex items-stretch hover:bg-stone-50">
              <button data-testid={`visit-row-${v.id}`} onClick={() => openVisit(v)} className="flex-1 text-left p-4 flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="font-bold">{v.vineyard_name} <span className="text-stone-400 font-normal">· Ranch {v.ranch_number}</span></div>
                  <div className="text-xs text-stone-500">{[v.client_name, v.visit_date, v.scout_name].filter(Boolean).join(" · ")}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs">{(v.blocks_done || []).length}/{(v.block_ids || []).length} blocks</span>
                  <span className={`text-[10px] font-bold uppercase px-2 py-1 ${v.status === "sent" ? "bg-[#7C9673] text-white" : v.status === "approved" ? "bg-[#1F3D34] text-white" : v.status === "scout_complete" ? "bg-amber-500 text-white" : "bg-stone-200 text-stone-600"}`}>
                    {v.status.replace("_", " ")}
                  </span>
                </div>
              </button>
              {user?.role === "admin" && (
                <button data-testid={`delete-visit-${v.id}`} title="Delete visit & notes"
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (!window.confirm(`Delete the ${v.visit_date} scouting visit at ${v.vineyard_name}? All its notes and photos will be removed.`)) return;
                    try { await api.delete(`/scouting/visits/${v.id}`); load(); }
                    catch (err) { alert(err.response?.data?.detail || "Could not delete visit"); }
                  }}
                  className="px-3 my-3 mr-3 border border-red-300 text-red-600 hover:bg-red-50 flex items-center">
                  <Trash2 size={15} />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Recent reports */}
      {dash.recent_reports.length > 0 && (
        <div className="bg-white border border-stone-200 p-4">
          <div className="font-bold uppercase text-sm mb-2 flex items-center gap-2"><FileText size={16} /> Recent Reports</div>
          {dash.recent_reports.map((r) => (
            <div key={r.id} className="flex items-center border-b border-stone-100 last:border-0 hover:bg-stone-50">
              <button onClick={() => navigate(`/scouting/report/${r.id}`)} className="flex-1 text-left flex justify-between py-1.5 text-sm">
                <span>{r.vineyard_name} — {r.report_date} <span className="text-stone-400">v{r.version}</span></span>
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 ${r.status === "sent" ? "bg-[#7C9673] text-white" : "bg-stone-200"}`}>{r.status}</span>
              </button>
              {user?.role === "admin" && (
                <button data-testid={`delete-report-${r.id}`} title="Delete report"
                  onClick={async () => {
                    if (!window.confirm(`Delete the ${r.report_date} report for ${r.vineyard_name}? If published, it will disappear from the client portal.`)) return;
                    try { await api.delete(`/scouting/reports/${r.id}`); load(); }
                    catch (err) { alert(err.response?.data?.detail || "Could not delete report"); }
                  }}
                  className="ml-2 p-1.5 border border-red-300 text-red-600 hover:bg-red-50">
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Vineyard cards */}
      <div>
        <h2 className="font-bold uppercase text-sm mb-2">Vineyards</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {dash.vineyards.map((v) => (
            <div key={v.ranch_number} className="bg-white border border-stone-200 p-4 space-y-2" data-testid={`vineyard-card-${v.ranch_number}`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-bold text-lg" style={{ fontFamily: "'Fraunces', serif" }}>{v.name}</div>
                  <div className="text-xs text-stone-500">{[v.client_name, `Ranch ${v.ranch_number}`, `${v.blocks} blocks`, v.acres ? `${v.acres} ac` : null].filter(Boolean).join(" · ")}</div>
                </div>
                {v.report_status && <span className={`text-[9px] font-bold uppercase px-2 py-1 ${v.report_status === "sent" ? "bg-[#7C9673] text-white" : "bg-stone-200"}`}>Report {v.report_status}</span>}
              </div>
              <div className="grid grid-cols-3 gap-1 text-center text-xs">
                <div className="bg-stone-50 p-2"><div className="text-[9px] uppercase font-bold text-stone-400">Last Scouted</div><div className="font-semibold">{v.last_scouted || "—"}</div></div>
                <div className="bg-stone-50 p-2"><div className="text-[9px] uppercase font-bold text-stone-400">Next</div><div className="font-semibold">{v.next_scouting || "—"}</div></div>
                <div className="bg-stone-50 p-2"><div className="text-[9px] uppercase font-bold text-stone-400">Open Tasks</div><div className="font-semibold">{v.open_tasks}</div></div>
              </div>
              <div className="flex gap-2">
                <button onClick={async () => { await openStart(); setForm((f) => ({ ...f, ranch_number: v.ranch_number, client_id: v.client_id || "" })); }}
                  className="flex-1 h-10 text-xs font-bold uppercase bg-[#1F3D34] text-white">Scout</button>
                <button onClick={() => openHistory(v)} className="flex-1 h-10 text-xs font-bold uppercase border border-stone-300 bg-white">Field Reports</button>
                <button data-testid={`timeline-btn-${v.ranch_number}`} onClick={async () => {
                  if (ranches.length === 0) { const r = await api.get("/ranches"); setRanches(r.data); }
                  setTimelineFor(v);
                }} className="flex-1 h-10 text-xs font-bold uppercase border border-stone-300 bg-white">Timeline</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Start scouting modal */}
      {form && (
        <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4">
          <div className="bg-white border border-stone-200 w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto" data-testid="start-scouting-modal">
            <div className="flex justify-between items-center">
              <h3 className="text-2xl font-black uppercase" style={{ fontFamily: "'Fraunces', serif" }}>Start Scouting</h3>
              <button onClick={() => setForm(null)}><X size={20} /></button>
            </div>
            <Field label="Client">
              <select data-testid="start-client-select" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} className="w-full h-12 px-3 border border-stone-300">
                <option value="">— Select Client (optional) —</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Vineyard">
              <select data-testid="start-vineyard-select" value={form.ranch_number} onChange={(e) => {
                const rn = e.target.value;
                const first = ranches.find((r) => r.ranch_number === rn);
                setForm({ ...form, ranch_number: rn, block_ids: [], client_id: form.client_id || first?.client_id || "" });
              }} className="w-full h-12 px-3 border border-stone-300">
                <option value="">— Select Vineyard —</option>
                {vineyards.map((r) => <option key={r.ranch_number} value={r.ranch_number}>{r.ranch_number} · {r.name}</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Date"><input type="date" data-testid="start-date-input" value={form.visit_date} onChange={(e) => setForm({ ...form, visit_date: e.target.value })} className="w-full h-12 px-3 border border-stone-300" /></Field>
              <Field label="Scout / Manager"><input value={user.name} disabled className="w-full h-12 px-3 border border-stone-200 bg-stone-50" /></Field>
            </div>
            {form.ranch_number && (
              <>
                <div className="flex gap-2">
                  <button data-testid="scope-entire-btn" onClick={() => setForm({ ...form, scope: "entire", block_ids: [] })}
                    className={`flex-1 h-12 font-bold uppercase text-sm border ${form.scope === "entire" ? "bg-[#1F3D34] text-white border-[#1F3D34]" : "bg-white border-stone-300"}`}>Entire Vineyard</button>
                  <button data-testid="scope-blocks-btn" onClick={() => setForm({ ...form, scope: "blocks" })}
                    className={`flex-1 h-12 font-bold uppercase text-sm border ${form.scope === "blocks" ? "bg-[#1F3D34] text-white border-[#1F3D34]" : "bg-white border-stone-300"}`}>Select Blocks</button>
                </div>
                {form.scope === "blocks" && (
                  <div className="grid grid-cols-2 gap-2">
                    {blocksFor(form.ranch_number).map((b) => {
                      const sel = form.block_ids.includes(b.id);
                      return (
                        <button key={b.id} onClick={() => setForm({ ...form, block_ids: sel ? form.block_ids.filter((x) => x !== b.id) : [...form.block_ids, b.id] })}
                          className={`h-12 px-2 text-sm font-semibold border ${sel ? "bg-[#7C9673] text-white border-[#7C9673]" : "bg-white border-stone-300"}`}>
                          {b.block || b.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </>
            )}
            <button data-testid="start-visit-btn" onClick={start} disabled={starting || !form.ranch_number || (form.scope === "blocks" && form.block_ids.length === 0)}
              className="w-full h-14 bg-[#1F3D34] text-white font-bold uppercase disabled:opacity-40">
              {starting ? "Starting…" : "Begin Scouting"}
            </button>
          </div>
        </div>
      )}

      {/* Report history modal */}
      {historyFor && (
        <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4">
          <div className="bg-white border border-stone-200 w-full max-w-md p-6 space-y-3 max-h-[85vh] overflow-y-auto" data-testid="report-history-modal">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-black uppercase" style={{ fontFamily: "'Fraunces', serif" }}>Field Reports — {historyFor.name}</h3>
              <button onClick={() => setHistoryFor(null)}><X size={20} /></button>
            </div>
            {history.length === 0 && <div className="text-sm text-stone-400 font-bold uppercase text-center py-4">No reports yet</div>}
            {history.map((r) => (
              <button key={r.id} onClick={() => navigate(`/scouting/report/${r.id}`)} className="w-full text-left flex justify-between items-center border border-stone-200 p-3 hover:bg-stone-50">
                <span className="text-sm font-semibold">{r.report_date} <span className="text-stone-400">v{r.version}</span></span>
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 ${r.status === "sent" ? "bg-[#7C9673] text-white" : "bg-stone-200"}`}>{r.status}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {/* Follow-up drill-down modal */}
      {followups !== null && (
        <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4">
          <div className="bg-white border border-stone-200 w-full max-w-2xl p-6 space-y-3 max-h-[85vh] overflow-y-auto" data-testid="followups-modal">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-black uppercase flex items-center gap-2" style={{ fontFamily: "'Fraunces', serif" }}>
                <AlertTriangle size={18} className="text-amber-500" /> Follow-Up Required
              </h3>
              <button data-testid="followups-close" onClick={() => setFollowups(null)}><X size={20} /></button>
            </div>
            {followups.length === 0 && <div className="text-sm text-stone-400 font-bold uppercase text-center py-4">Nothing flagged for follow-up</div>}
            {followups.map((f, idx) => (
              <button key={idx} data-testid={`followup-row-${idx}`}
                onClick={() => { setFollowups(null); navigate(f.visit_status === "scout_complete" && isManager ? `/scouting/review/${f.visit_id}` : `/scouting/visit/${f.visit_id}`); }}
                className="w-full text-left border border-stone-200 p-3 hover:border-[#1F3D34] space-y-1">
                <div className="flex flex-wrap justify-between items-center gap-2">
                  <span className="font-bold text-sm">{f.vineyard_name} · {f.block_label}</span>
                  <span className="text-xs text-stone-500">Ranch {f.ranch_number} · {f.visit_date}</span>
                </div>
                {f.items.map((it, k) => (
                  <div key={k} className="text-xs text-stone-600">
                    <span className="font-bold text-amber-700">{it.kind}:</span> {it.name}
                    {it.severity ? ` — ${it.severity}` : ""}{it.location ? ` @ ${it.location}` : ""}{it.comment ? ` · ${it.comment}` : ""}
                  </div>
                ))}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Timeline block picker */}
      {timelineFor && (
        <div className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4">
          <div className="bg-white border border-stone-200 w-full max-w-md p-6 space-y-3" data-testid="timeline-picker-modal">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-black uppercase" style={{ fontFamily: "'Fraunces', serif" }}>Season Timeline — {timelineFor.name}</h3>
              <button onClick={() => setTimelineFor(null)}><X size={20} /></button>
            </div>
            <div className="text-xs text-stone-500 font-semibold uppercase">Select a block</div>
            <div className="grid grid-cols-2 gap-2">
              {ranches.filter((r) => r.ranch_number === timelineFor.ranch_number).map((b) => (
                <button key={b.id} data-testid={`timeline-block-${b.id}`} onClick={() => navigate(`/scouting/timeline/${b.id}`)}
                  className="h-12 px-2 text-sm font-semibold border border-stone-300 bg-white hover:border-[#1F3D34]">
                  {b.block || b.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
